import React, { useContext, useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Typography,
  Row,
  Col,
  List,
  Avatar,
  Button,
  Timeline,
  Popconfirm,
  InputNumber,
} from 'antd';
import { Timestamp } from 'firebase/firestore';
import { useIntl } from 'react-intl';
import PlayerProfileDrawer from '../profile/playerProfileDrawer';
import { StoreContext } from '../store/index';
import { updateShift } from '../shifts/api';
import { getDateFromTimestamp } from '../utils';
import CardIcon from '../components/CardIcon';
import { getScore } from '../shifts/utils';

function StatsTab({ shift, timer, onStatChangeInFinishedGame }) {
  const store = useContext(StoreContext);
  const {
    user: [user],
  } = store;
  const { formatMessage } = useIntl();
  const { Text } = Typography;

  const {
    startDate,
    placeName,
    address,
    placeSport,
    totalPlayers,
    localTeam,
    price,
  } = shift;

  const [form] = Form.useForm();

  const [showPlayerProfile, setShowPlayerProfile] = useState(false);
  const [playerProfileId, setPlayerProfileId] = useState('false');
  const [loading, setLoading] = useState(false);
  const [minutesField, setMinutesField] = useState(0);

  const handleAddStat = (
    userId,
    playerName,
    isLocal,
    minute,
    goals,
    yellowCard,
    redCard,
  ) => {
    const st = [
      ...shift.stats,
      {
        shiftId: shift.id,
        userId,
        playerName,
        isLocal,
        goals,
        yellowCard,
        redCard,
        absent: false,
        gameMinute: minute,
        date: Timestamp.fromDate(new Date()),
      },
    ];
    updateShift(
      shift.id,
      {
        stats: st,
      },
      setLoading,
    ).then(() => {
      if (shift.isFinished) {
        onStatChangeInFinishedGame(st);
      }
    });
  };

  const handleRemoveStat = date => {
    const st = [...shift.stats.filter(i => i.date.seconds !== date.seconds)];
    updateShift(
      shift.id,
      {
        stats: st,
      },
      setLoading,
    ).then(() => {
      if (shift.isFinished) {
        onStatChangeInFinishedGame(st);
      }
    });
  };
  const removeRowWraper = (children, date) =>
    user.isAdmin ? (
      <Popconfirm
        title={formatMessage({
          id: 'shifts.actions.stats.row.delete.confirm.title',
        })}
        onConfirm={() => handleRemoveStat(date)}
        onCancel={() => {}}
        okText={formatMessage({ id: 'common.yes' })}
        cancelText={formatMessage({ id: 'common.no' })}>
        {children}
      </Popconfirm>
    ) : (
      children
    );

  const getTimelineLogs = () =>
    shift.stats
      .sort((a, b) => a.gameMinute - b.gameMinute)
      .map(
        ({
          playerName,
          goals,
          yellowCard,
          redCard,
          gameMinute,
          userId,
          date,
        }) => {
          if (goals > 0) {
            return {
              children: removeRowWraper(
                <div
                  style={{
                    padding: '8px 8px 8px 8x',
                    margin: '8px 8px 8px 8x',
                  }}>
                  {playerName}
                </div>,
                date,
              ),
              label: removeRowWraper(`${gameMinute}'`, date),
              color: 'green',
              dot: removeRowWraper('⚽', date),
            };
          }
          if (yellowCard > 0) {
            return {
              children: removeRowWraper(playerName, date),
              label: removeRowWraper(`${gameMinute}'`, date),
              dot: removeRowWraper(
                <div>
                  <CardIcon color="yellow" />
                </div>,
                date,
              ),
            };
          }
          if (redCard) {
            return {
              children: removeRowWraper(playerName, date),
              label: removeRowWraper(`${gameMinute}'`, date),
              dot: removeRowWraper(
                <div>
                  <CardIcon color="red" />
                </div>,
                date,
              ),
            };
          }
          return {
            children: goals,
            label: `${gameMinute}'`,
            dot: '⚽',
          };
        },
      );

  const renderList = local => (
    <Col xs={12}>
      <List
        size="small"
        itemLayout="vertical"
        loading={loading}
        open
        dataSource={shift.players.filter(({ isLocal }) => isLocal === local)}
        renderItem={item => (
          <List.Item
            style={{ padding: '0px', marginTop: '8px' }}
            key={item.userId}>
            <List.Item.Meta
              avatar={
                <Avatar
                  src={item.photoUrl}
                  onClick={() => {
                    setPlayerProfileId(item.userId);
                    setShowPlayerProfile(true);
                  }}
                />
              }
              title={
                <Row justify="space-between">
                  <Col>{item.name}</Col>
                </Row>
              }
              description={
                <Row align="middle" justify="center" style={{ width: '80%' }}>
                  <Col xs={8}>
                    <Popconfirm
                      title={formatMessage({
                        id: 'shifts.actions.stats.goal.confirm.title',
                      })}
                      description={
                        <InputNumber
                          min={1}
                          max={130}
                          formatter={value => `${value} Min`}
                          parser={value => value?.replace(' Min', '')}
                          placeholder="Min"
                          value={minutesField}
                          onChange={e => setMinutesField(e)}
                        />
                      }
                      onOpenChange={() =>
                        setMinutesField(new Date(timer).getMinutes())
                      }
                      onConfirm={() => {
                        setMinutesField(1);
                        handleAddStat(
                          item.userId,
                          item.name,
                          item.isLocal,
                          minutesField,
                          1,
                          0,
                          false,
                        );
                      }}
                      onCancel={() => setMinutesField(1)}
                      okText={formatMessage({ id: 'common.yes' })}
                      cancelText={formatMessage({ id: 'common.no' })}>
                      <Button
                        style={{
                          padding: '4px',
                        }}
                        loading={loading}>
                        ⚽
                      </Button>
                    </Popconfirm>
                  </Col>
                  <Col xs={8}>
                    <Popconfirm
                      title={formatMessage({
                        id: 'shifts.actions.stats.card.red.confirm.title',
                      })}
                      description={
                        <InputNumber
                          min={1}
                          max={130}
                          formatter={value => `${value} Min`}
                          parser={value => value?.replace(' Min', '')}
                          placeholder="Min"
                          value={minutesField}
                          onChange={e => setMinutesField(e)}
                        />
                      }
                      onConfirm={() =>
                        handleAddStat(
                          item.userId,
                          item.name,
                          item.isLocal,
                          minutesField,
                          0,
                          0,
                          true,
                        )
                      }
                      onCancel={() => {}}
                      okText={formatMessage({ id: 'common.yes' })}
                      cancelText={formatMessage({ id: 'common.no' })}>
                      <Button
                        style={{
                          padding: '4px',
                        }}
                        loading={loading}>
                        <CardIcon color="red" />
                      </Button>
                    </Popconfirm>
                  </Col>
                  <Col xs={8}>
                    <Popconfirm
                      title={formatMessage({
                        id: 'shifts.actions.stats.card.yellow.confirm.title',
                      })}
                      description={
                        <InputNumber
                          min={1}
                          max={130}
                          formatter={value => `${value} Min`}
                          parser={value => value?.replace(' Min', '')}
                          placeholder="Min"
                          value={minutesField}
                          onChange={e => setMinutesField(e)}
                        />
                      }
                      onConfirm={() =>
                        handleAddStat(
                          item.userId,
                          item.name,
                          item.isLocal,
                          minutesField,
                          0,
                          1,
                          false,
                        )
                      }
                      onCancel={() => {}}
                      okText={formatMessage({ id: 'common.yes' })}
                      cancelText={formatMessage({ id: 'common.no' })}>
                      <Button
                        style={{
                          padding: '4px',
                        }}
                        loading={loading}>
                        <CardIcon color="yellow" />
                      </Button>
                    </Popconfirm>
                  </Col>
                </Row>
              }
            />
          </List.Item>
        )}
      />
    </Col>
  );

  return (
    <Col>
      {showPlayerProfile && playerProfileId && (
        <PlayerProfileDrawer
          playerId={playerProfileId}
          visible={showPlayerProfile}
          onClose={() => setShowPlayerProfile(false)}
        />
      )}
      <Timeline
        style={{
          paddingTop: '4px',
          overflow: 'auto',
          maxHeight: '200px',
        }}
        mode="right"
        items={getTimelineLogs()}
      />
      <div
        style={{
          overflow: 'auto',
          maxHeight: '300px',
          scrollbarWidth: 'thin',
        }}>
        {(user.isAdmin || user.uid === shift.userId) && (
          <Row>
            {renderList(true)}
            {renderList(false)}
          </Row>
        )}
      </div>
    </Col>
  );
}

export default StatsTab;
